import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle, X } from 'lucide-react';

const ConfirmActionAlert = ({
    isOpen,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "info", // danger, success, warning, info
    isLoading = false,
}) => {
    const [isAnimate, setIsAnimate] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsAnimate(true);
        } else {
            setIsAnimate(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const variants = {
        danger: {
            icon: <XCircle className="w-6 h-6 text-red-600" />,
            bg: "bg-red-100",
            button: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
            titleColor: "text-red-900"
        },
        success: {
            icon: <CheckCircle className="w-6 h-6 text-green-600" />,
            bg: "bg-green-100",
            button: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
            titleColor: "text-green-900"
        },
        warning: {
            icon: <AlertTriangle className="w-6 h-6 text-amber-600" />,
            bg: "bg-amber-100",
            button: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
            titleColor: "text-amber-900"
        },
        info: {
            icon: <Info className="w-6 h-6 text-blue-600" />,
            bg: "bg-blue-100",
            button: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
            titleColor: "text-blue-900"
        }
    };

    const currentVariant = variants[variant] || variants.info;

    return (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${isAnimate ? 'opacity-100' : 'opacity-0'}`}>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
                onClick={onCancel}
            />

            {/* Modal */}
            <div
                className={`relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all duration-300 ${isAnimate ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3">
                    <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-full ${currentVariant.bg} flex items-center justify-center`}>
                            {currentVariant.icon}
                        </div>
                        <button
                            onClick={onCancel}
                            className="text-gray-400 hover:text-gray-500 transition-colors p-1"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="mt-3">
                        <h3 className={`text-xl font-bold ${currentVariant.titleColor} mb-2`}>
                            {title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                            {message}
                        </p>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row-reverse gap-3">
                        <button
                            onClick={onConfirm}
                            disabled={isLoading}
                            className={`w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-white rounded-lg shadow-lg transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center ${currentVariant.button} ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </>
                            ) : confirmText}
                        </button>
                        <button
                            onClick={onCancel}
                            disabled={isLoading}
                            className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                        >
                            {cancelText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmActionAlert;